import React, { useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, LogOut, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) return; // preserve in-page anchor navigation
    // Reset both the window and the main scroll container.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [pathname, hash]);
  return null;
}

const MOBILE_NAV = [
  { to: "/", label: "Accueil" },
  { to: "/creer-mon-support", label: "Je veux créer un support" },
  { to: "/preparer-mon-projet", label: "J'ai déjà mon projet" },
  { to: "/services", label: "Toutes les prestations" },
  { to: "/mise-en-page", label: "Mise en page & édition" },
  { to: "/services/couvertures", label: "Couvertures de livres" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/a-propos", label: "À propos" },
  { to: "/aide-choix", label: "Je ne sais pas quoi choisir" },
  { to: "/demande-devis", label: "Demander un devis" },
];

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/creer-mon-support", label: "Créer un support" },
  { to: "/preparer-mon-projet", label: "J'ai déjà mon projet" },
  { to: "/mise-en-page", label: "Mise en page" },
  { to: "/services/couvertures", label: "Couvertures" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/a-propos", label: "À propos" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  React.useEffect(() => { setOpen(false); }, [loc.pathname]);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    nav("/connexion", { replace: true });
  };
  const accountLabel = user ? (user.role === "admin" ? "Back-office" : "Mon espace") : "Se connecter";
  const accountTo = user ? (user.role === "admin" ? "/admin" : "/espace") : "/connexion";

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF8F5]/85 border-b border-black/10" data-testid="site-header">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="group flex items-center gap-4" data-testid="brand-link">
          <img src="/logo.webp" alt="Charbonier Studio" className="h-40 w-40 object-contain -my-6" />
        </Link>

        <nav className="hidden lg:flex items-center text-sm" data-testid="main-nav">
          {NAV.slice(1).map((n, i) => (
            <React.Fragment key={n.to}>
              {i > 0 && <span aria-hidden className="mx-4 h-4 w-px bg-black/15" />}
              <NavLink to={n.to} end={n.to === "/services"}
                data-testid={`nav-${n.to.replace(/\//g, "-")}`}
                className={({ isActive }) => `no-underline transition-colors ${isActive ? "text-[#0E7C90]" : "text-neutral-700 hover:text-black"}`}>
                {n.label}
              </NavLink>
            </React.Fragment>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/sos-print" data-testid="sos-cta" className="cs-btn-primary !py-2 !px-4 text-sm">
            SOS Print <ArrowUpRight size={16} />
          </Link>
          <div className="flex flex-col items-stretch gap-1">
            <Link to={accountTo} data-testid="account-cta"
              className="text-sm px-4 py-2 border border-black/70 hover:bg-black hover:text-[#FAF8F5] transition-colors text-center">
              {accountLabel}
            </Link>
            <Link
              to="/aide-choix"
              data-testid="header-assistant-cta"
              className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500 hover:text-[#0E7C90] transition-colors inline-flex items-center justify-center gap-1"
            >
              <Sparkles size={11} /> Me laisser guider
            </Link>
          </div>
          {user && (
            <button onClick={handleLogout} data-testid="header-logout"
              className="text-sm px-3 py-2 border border-black/25 hover:border-black hover:bg-black hover:text-[#FAF8F5] transition-colors inline-flex items-center gap-2"
              title={`Déconnecter ${user.email}`}>
              <LogOut size={14} /> Déconnexion
            </button>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <div className="flex flex-col items-stretch gap-1">
            <Link
              to={accountTo}
              data-testid="mobile-header-account"
              className="text-[13px] px-3 py-2 border border-black/70 hover:bg-black hover:text-[#FAF8F5] transition-colors whitespace-nowrap text-center"
            >
              {accountLabel}
            </Link>
            <Link
              to="/aide-choix"
              data-testid="mobile-header-assistant"
              className="text-[10px] font-mono uppercase tracking-[0.14em] text-neutral-500 hover:text-[#0E7C90] transition-colors inline-flex items-center justify-center gap-1"
            >
              <Sparkles size={10} /> Me laisser guider
            </Link>
          </div>
          <button className="p-2" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-black/10 bg-[#FAF8F5] overflow-y-auto overscroll-contain"
          style={{ maxHeight: "calc(100dvh - 72px)" }}
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-1 pb-[max(env(safe-area-inset-bottom),16px)]">
            {user && (
              <div className="mb-3 flex flex-col gap-2">
                <div className="text-[12px] font-mono uppercase tracking-[0.14em] text-neutral-500 px-1">
                  Connecté · {user.email}
                </div>
                <div className="flex gap-2">
                  <Link
                    to={accountTo}
                    data-testid="mobile-account-cta"
                    className="flex-1 text-center py-3 border border-black/70 text-[15px] hover:bg-black hover:text-[#FAF8F5] transition-colors"
                  >
                    {accountLabel}
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-logout"
                    className="flex-1 text-center py-3 border border-black/25 inline-flex items-center justify-center gap-2 hover:bg-black hover:text-[#FAF8F5] transition-colors text-[15px]"
                  >
                    <LogOut size={14} /> Déconnexion
                  </button>
                </div>
              </div>
            )}
            <Link to="/sos-print" className="cs-btn-primary text-sm justify-center mb-3" data-testid="mobile-sos-cta">
              SOS Print · imprimeur refuse mon fichier <ArrowUpRight size={16} />
            </Link>
            {MOBILE_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/services" || n.to === "/"}
                className={({ isActive }) => `py-2.5 text-[15px] border-b border-black/5 ${isActive ? "text-[#0E7C90]" : "text-neutral-800"}`}>
                {n.label}
              </NavLink>
            ))}
            {!user && (
              <div className="pt-4">
                <Link
                  to={accountTo}
                  data-testid="mobile-account-cta"
                  className="block text-center py-3 border border-black/70 text-[15px] hover:bg-black hover:text-[#FAF8F5] transition-colors"
                >
                  {accountLabel}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="cs-dark cs-grain mt-24" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-4">
            <img src="/logo.webp" alt="Charbonier Studio" className="h-16 w-16 object-contain" />
            <div className="font-sans font-semibold text-2xl tracking-[0.1em] uppercase">Charbonier<br /><span className="font-mono text-xs tracking-[0.32em] text-neutral-400">Studio</span></div>
          </div>
          <p className="mt-6 text-sm text-neutral-400 max-w-md leading-relaxed">
            Création graphique, mise en page & fichiers prêts à imprimer. Du fichier à l'impression,
            Charbonier Studio prépare vos créations aux bonnes dimensions, dans les bons formats et selon
            les exigences de votre imprimeur.
          </p>
        </div>
        <div>
          <div className="cs-eyebrow text-neutral-500 mb-4">Navigation</div>
          <ul className="space-y-2 text-sm">
            {NAV.slice(0, 6).map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-[#0E7C90]">{n.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="cs-eyebrow text-neutral-500 mb-4">Studio</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/a-propos" className="hover:text-[#0E7C90]">À propos</Link></li>
            <li><Link to="/tarifs" className="hover:text-[#0E7C90]">Tarifs</Link></li>
            <li><Link to="/aide-choix" className="hover:text-[#0E7C90]">Je ne sais pas quoi choisir</Link></li>
            <li><Link to="/demande-devis" className="hover:text-[#0E7C90]">Demander un devis</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs font-mono text-neutral-500">
          <div>© {new Date().getFullYear()} Charbonier Studio — Atelier graphique indépendant</div>
          <ul className="flex flex-wrap gap-x-5 gap-y-2" data-testid="footer-legal">
            <li><Link to="/mentions-legales" className="hover:text-[#0E7C90]">Mentions légales</Link></li>
            <li><Link to="/cgv" className="hover:text-[#0E7C90]">CGV</Link></li>
            <li><Link to="/confidentialite" className="hover:text-[#0E7C90]">Confidentialité</Link></li>
            <li><Link to="/espace/contact" className="hover:text-[#0E7C90]">Contact</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-[70vh]">
        <Outlet />
      </main>
      <Footer />
    </>
  );
}
